import React, { useEffect, useState } from "react";
import { api, formatApiError , toastError} from "../../lib/api";
import { PageHeader, Pill } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["owner_admin", "admin_support", "manager_trainer", "representative", "viewer"];

export default function Memberships() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", tenant_role: "representative", company_id: null, branch_id: null, team_id: null });

  const load = async () => {
    try {
      const [m, u, c, b, t] = await Promise.all([
        api.get("/tenant/memberships"), api.get("/tenant/users"),
        api.get("/tenant/companies"), api.get("/tenant/branches"), api.get("/tenant/teams"),
      ]);
      setRows(m.data); setUsers(u.data); setCompanies(c.data); setBranches(b.data); setTeams(t.data);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/tenant/memberships", form, { headers: { "Idempotency-Key": crypto.randomUUID() } });
      toast.success("Membership created");
      setOpen(false);
      setForm({ user_id: "", tenant_role: "representative", company_id: null, branch_id: null, team_id: null });
      load();
    } catch (e) { toastError(e); }
  };

  const userName = (id) => users.find((u) => u.id === id)?.email || id.slice(0, 8);

  return (
    <>
      <PageHeader title="Memberships" subtitle="Assign roles and scope"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-membership-btn"><Plus className="w-4 h-4 mr-1" /> New membership</Button>
            </DialogTrigger>
            <DialogContent data-testid="create-membership-dialog">
              <DialogHeader><DialogTitle>New membership</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>User</Label>
                  <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="mem-user"><SelectValue placeholder="Choose user" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Role</Label>
                  <Select value={form.tenant_role} onValueChange={(v) => setForm({ ...form, tenant_role: v })}>
                    <SelectTrigger data-testid="mem-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Company (optional)</Label>
                  <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="mem-company"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— none —</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Branch (optional)</Label>
                  <Select value={form.branch_id || "none"} onValueChange={(v) => setForm({ ...form, branch_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="mem-branch"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— none —</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Team (optional)</Label>
                  <Select value={form.team_id || "none"} onValueChange={(v) => setForm({ ...form, team_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="mem-team"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— none —</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.user_id} data-testid="mem-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="memberships-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">User</th>
                <th className="text-left px-4 py-2.5 font-medium">Role</th>
                <th className="text-left px-4 py-2.5 font-medium">Company</th>
                <th className="text-left px-4 py-2.5 font-medium">Branch</th>
                <th className="text-left px-4 py-2.5 font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-t border-slate-200" data-testid={`mem-row-${m.id.slice(0,8)}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900" data-testid={`mem-user-name-${m.id.slice(0,8)}`}>
                      {m.user_name || userName(m.user_id)}
                    </div>
                    {m.user_email && (
                      <div className="text-xs text-slate-500">{m.user_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5"><Pill value={m.tenant_role || m.platform_role} type="role" /></td>
                  <td className="px-4 py-2.5" data-testid={`mem-company-${m.id.slice(0,8)}`}>
                    {m.company_name ? (
                      <span>{m.company_name}</span>
                    ) : m.company_id ? (
                      <span className="font-mono text-xs text-slate-500">{m.company_id.slice(0,8)}…</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`mem-branch-${m.id.slice(0,8)}`}>
                    {m.branch_name ? (
                      <span>{m.branch_name}</span>
                    ) : m.branch_id ? (
                      <span className="font-mono text-xs text-slate-500">{m.branch_id.slice(0,8)}…</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {m.team_name && (
                      <div className="text-xs text-slate-500">Team: {m.team_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">{m.active ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
